import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Mail, Printer } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { cardBrandLabel, formatDate, formatMoney, ReceiptDetail, selfServiceError } from '../../lib/patientSelfService';
import { useTrackWhen } from '../../components/analytics/useTrackWhen';

// The portal chrome (header, tab bar) isn't this page's to change, so printing
// hides everything but the receipt rather than restyling the layout.
const PRINT_CSS = `@media print {
  body * { visibility: hidden !important; }
  #patient-receipt, #patient-receipt * { visibility: visible !important; }
  #patient-receipt { position: absolute; inset: 0 auto auto 0; width: 100%; box-shadow: none !important; border: 0 !important; background: #fff !important; color: #000 !important; }
  @page { margin: 16mm; }
}`;

/** One payment, printable. */
export function DtpReceipt() {
  const { physicianId, receiptId } = useDtpParams<{ physicianId: string; receiptId: string }>();
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const receipt = useQuery({ queryKey: ['patient-receipt', receiptId, patientId], retry: false, gcTime: 0, enabled: Boolean(receiptId), queryFn: () => dtpApi.getReceipt(receiptId!) });
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  useTrackWhen(receipt.isSuccess, 'receipt_viewed', { receiptId });

  async function email() {
    setSending(true); setMessage(null);
    try {
      const { sentTo } = await dtpApi.emailReceipt(receiptId!);
      setMessage({ ok: true, text: `Receipt sent to ${sentTo}.` });
    } catch (err) {
      setMessage({ ok: false, text: selfServiceError(err, 'We couldn’t send the receipt. Please try again later.') });
    } finally { setSending(false); }
  }

  const back = <Link to={dtpPath(physicianId, '/billing')} className="inline-flex h-10 items-center gap-1.5 rounded-full px-1 text-sm font-medium text-[var(--portal-muted)] hover:text-[var(--portal-ink)] print:hidden"><ArrowLeft aria-hidden="true" className="h-4 w-4" />Billing</Link>;
  if (receipt.isPending) return <div className="mx-auto max-w-2xl">{back}<p role="status" className="mt-6 text-sm text-[var(--portal-muted)]">Loading receipt…</p></div>;
  if (receipt.isError || !receipt.data) return <div className="mx-auto max-w-2xl">{back}<div role="alert" className="mt-6 rounded-2xl bg-[#fde4dc] p-4 text-sm text-[#8c2a14]">We couldn’t find this receipt. <button className="min-h-[44px] font-semibold underline" onClick={() => receipt.refetch()}>Try again</button></div></div>;

  const r: ReceiptDetail = receipt.data;
  const fullyRefunded = r.refund?.status === 'refunded';
  return <div className="mx-auto max-w-2xl">
    <style>{PRINT_CSS}</style>
    <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
      {back}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => window.print()} className="inline-flex h-10 items-center gap-2 rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] hover:bg-white"><Printer aria-hidden="true" className="h-4 w-4" />Print</button>
        {!impersonated && r.canEmail && !fullyRefunded && <button type="button" onClick={email} disabled={sending}
          className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90 disabled:opacity-60">
          {sending ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <Mail aria-hidden="true" className="h-4 w-4" />}Email me this receipt
        </button>}
      </div>
    </div>
    {message && <p role={message.ok ? 'status' : 'alert'} className={`mt-3 text-sm print:hidden ${message.ok ? 'text-green-700' : 'rounded-lg bg-red-50 p-3 text-red-700'}`}>{message.text}</p>}

    <article id="patient-receipt" aria-labelledby="receipt-title" className="portal-card mt-5 p-6 sm:p-8">
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-[var(--portal-line)] pb-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--portal-muted)]">Receipt</p>
          <h1 id="receipt-title" className="portal-display mt-1 text-2xl font-bold">{r.clinicName || 'Your clinic'}</h1>
          {r.clinicPhone && <p className="text-sm text-[var(--portal-muted)]">{r.clinicPhone}</p>}
        </div>
        <div className="text-right">
          <p className="portal-display text-3xl font-bold tabular-nums">{formatMoney(r.amountPaid)}</p>
          <p className="text-sm text-[var(--portal-muted)]">Paid {formatDate(r.paidAt, { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
      </header>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 py-5 text-sm sm:grid-cols-2">
        <Field label="Billed to">{r.patientName}</Field>
        <Field label="For">{r.description}</Field>
        <Field label="Order number">#{r.orderNumber}</Field>
        {r.invoiceNumber && <Field label="Invoice number">{r.invoiceNumber}</Field>}
        <Field label="Payment method">{r.card ? `${cardBrandLabel(r.card.brand)} •••• ${r.card.last4}` : 'Card'}</Field>
        <Field label="On your statement as">{r.statementName}</Field>
      </dl>

      <table className="w-full border-t border-[var(--portal-line)] text-sm">
        <caption className="sr-only">Items paid for</caption>
        <thead><tr className="text-left text-xs uppercase tracking-[0.08em] text-[var(--portal-muted)]"><th scope="col" className="py-3 font-semibold">Item</th><th scope="col" className="py-3 text-right font-semibold">Amount</th></tr></thead>
        <tbody className="divide-y divide-[var(--portal-line)]">
          {r.items.map((item: ReceiptDetail['items'][number], index: number) => <tr key={`${item.productId}-${index}`}>
            <td className="py-2.5 pr-4">{item.productName}{item.quantity > 1 ? <span className="text-[var(--portal-muted)]"> × {item.quantity}</span> : null}</td>
            <td className="py-2.5 text-right tabular-nums">{formatMoney(item.totalPrice)}</td>
          </tr>)}
        </tbody>
        <tfoot className="border-t border-[var(--portal-line)]">
          {(r.shippingAmount > 0 || r.visitFee > 0) && <Row label="Subtotal" value={r.subtotal} />}
          {r.shippingAmount > 0 && <Row label="Shipping" value={r.shippingAmount} />}
          {r.visitFee > 0 && <Row label="Visit fee" value={r.visitFee} />}
          <tr className="text-base font-semibold"><th scope="row" className="pt-3 text-left">Total</th><td className="pt-3 text-right tabular-nums">{formatMoney(r.total)}</td></tr>
          {r.refund && <tr className="text-[#7a4a00]"><th scope="row" className="pt-2 text-left font-medium">{fullyRefunded ? 'Refunded in full' : 'Refunded'}</th><td className="pt-2 text-right tabular-nums">−{formatMoney(r.refund.amount)}</td></tr>}
        </tfoot>
      </table>
      {r.amountPaid !== r.total && <p className="mt-4 text-xs text-[var(--portal-muted)]">Amount charged to your card: {formatMoney(r.amountPaid)}.</p>}
    </article>
  </div>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div><dt className="text-xs text-[var(--portal-muted)]">{label}</dt><dd className="mt-0.5 font-medium">{children}</dd></div>;
}
function Row({ label, value }: { label: string; value: number }) {
  return <tr><th scope="row" className="pt-2 text-left font-normal text-[var(--portal-muted)]">{label}</th><td className="pt-2 text-right tabular-nums">{formatMoney(value)}</td></tr>;
}

export default DtpReceipt;
